import { useEffect, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import type { StepBand } from './categoryBands';
import { loadPdf, renderPdfPageBand } from './pdfRender';

// EACH STEP'S PAGE SECTION, rendered off the PDF — no model, shown as soon as Phase 1 has run.
// The pictures a step's calls are shown, and so the pictures their badges show beside the
// prompt: Phase 5's analysis and hardware table and Phase 6's hole-finding all read them,
// rendered the same way.
//
// RENDERED ONCE PER FILE AND STEP, whoever asks. The badges sit over the subphases now, so a
// phase mounts several readers of the same sections at once — and each rendering every band
// of the manual again at 1300 px was the same work two or three times over. A step's key is its
// pages and their bands, so a re-classified step is a different key and is rendered afresh.
const rendered = new WeakMap<Blob, Map<string, Promise<string[]>>>();

const stepKey = (step: StepBand) =>
  JSON.stringify([step.category, step.pages, step.pages.map((p) => step.bands[p] ?? null)]);

export function useStepSections(file: Blob, steps: StepBand[]): Record<string, string[]> {
  const [sections, setSections] = useState<Record<string, string[]>>({});
  useEffect(() => {
    let cancelled = false;
    setSections({});
    let byStep = rendered.get(file);
    if (!byStep) {
      byStep = new Map();
      rendered.set(file, byStep);
    }
    const cache = byStep;
    // The document is opened only if a step here has not been rendered yet, and closed once
    // what this call started has landed.
    let task: PDFDocumentLoadingTask | null = null;
    let doc: Promise<PDFDocumentProxy> | null = null;
    const open = () => {
      if (!doc) {
        doc = file.arrayBuffer().then((buf) => {
          task = loadPdf(buf);
          return task.promise;
        });
      }
      return doc;
    };
    const started: Promise<string[]>[] = [];
    (async () => {
      const out: Record<string, string[]> = {};
      for (const step of steps) {
        const key = stepKey(step);
        let imgs = cache.get(key);
        if (!imgs) {
          imgs = (async () => {
            const doc = await open();
            const got: string[] = [];
            for (const page of step.pages) {
              const band = step.bands[page] ?? { top: 0, bottom: 1 };
              const img = await renderPdfPageBand(doc, page, band.top, band.bottom, 1300);
              if (img) got.push(img);
            }
            return got;
          })();
          // A failed render is not kept: the next reader tries again.
          imgs.catch(() => cache.delete(key));
          cache.set(key, imgs);
          started.push(imgs);
        }
        out[step.category] = await imgs.catch(() => []);
        if (cancelled) break;
        setSections({ ...out });
      }
      await Promise.allSettled(started);
      if (task) void (task as PDFDocumentLoadingTask).destroy();
    })();
    return () => {
      cancelled = true;
    };
  }, [file, steps]);
  return sections;
}
